import { Link, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { useFormik } from "formik"
import Button from "@/components/Button"
import TextField from "@/components/TextField"
import { Routes } from "@/core/routing/routes"
import FormFieldErrorMessage from "@/components/FormFieldErrorMessage"
import validationSchema from "./validation-schema"
import { useLogin } from "@/hooks/useLogin"
import {
  clerkAuthConfigured,
  getClerk,
  legacyPasswordLoginEnabled,
} from "@/core/auth/clerk"
import { useAppDispatch } from "@/redux/hooks"
import { setToast } from "@/redux/toastSlice"

const dreamsenderRedirectUrl = () =>
  typeof window === "undefined"
    ? Routes.organizations
    : new URL(Routes.organizations, window.location.origin).toString()

const LoginForm = () => {
  const { loginUser, response } = useLogin()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isClerkConfigured = clerkAuthConfigured()
  const showLegacyLogin = legacyPasswordLoginEnabled()
  const [clerkLoading, setClerkLoading] = useState(isClerkConfigured)
  const {
    handleSubmit,
    getFieldProps,
    errors: { email: emailError, password: passwordError },
    touched: { email: emailTouched, password: passwordTouched },
  } = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: ({ email, password }) => {
      loginUser({ email, password })
    },
  })

  useEffect(() => {
    let active = true

    if (!isClerkConfigured) {
      setClerkLoading(false)
      return
    }

    getClerk()
      .then(() => {
        if (active) setClerkLoading(false)
      })
      .catch(() => {
        if (active) setClerkLoading(false)
      })

    return () => {
      active = false
    }
  }, [isClerkConfigured])

  const handleClerkSignIn = async () => {
    try {
      setClerkLoading(true)
      const clerk = await getClerk()
      if (!clerk) throw new Error("Clerk is not configured")
      const redirectUrl = dreamsenderRedirectUrl()
      const signInUrl = clerk.buildSignInUrl({
        signInFallbackRedirectUrl: redirectUrl,
        signInForceRedirectUrl: redirectUrl,
        signUpFallbackRedirectUrl: redirectUrl,
        signUpForceRedirectUrl: redirectUrl,
      })
      window.location.assign(signInUrl)
    } catch {
      setClerkLoading(false)
      dispatch(
        setToast({
          show: true,
          variant: "error",
          message: "Unable to start Clerk sign in",
        }),
      )
    }
  }

  return (
    <div className="flex flex-col gap-5 w-full">
      {isClerkConfigured && (
        <Button
          fullWidth
          disabled={clerkLoading}
          loading={clerkLoading}
          type="button"
          onClick={handleClerkSignIn}
        >
          Continue with Inboxing
        </Button>
      )}
      {isClerkConfigured && showLegacyLogin && (
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <div className="h-px flex-1 bg-gray-200" />
          <span>or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
      )}
      {showLegacyLogin && (
        <form className="flex flex-col gap-5 w-full" onSubmit={handleSubmit}>
          <TextField
            type="email"
            placeholder="Enter your email address"
            topLeftLabel="Email Address"
            tabIndex={0}
            bottomLeftLabel={
              <FormFieldErrorMessage
                error={emailError}
                touched={emailTouched}
              />
            }
            {...getFieldProps("email")}
          />
          <TextField
            type="password"
            placeholder="Enter your password"
            topLeftLabel="Password"
            topRightLabel={
              <Link
                tabIndex={-1}
                to={Routes.forgotPassword.form}
                className="text-xs underline sm:text-sm text-black font-normal"
              >
                Forgot Password?
              </Link>
            }
            bottomLeftLabel={
              <FormFieldErrorMessage
                error={passwordError}
                touched={passwordTouched}
              />
            }
            tabIndex={0}
            {...getFieldProps("password")}
          />
          <Button
            fullWidth
            disabled={response.isLoading}
            className="mt-1"
            type="submit"
          >
            Login
          </Button>
        </form>
      )}
    </div>
  )
}

export default LoginForm
